// FirstPassword.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import api from "../../constants/api";
import { useNavigate } from "react-router-dom";
import { showError, promise, handleError } from "../../utils/toast.js";
import { Lock, Eye, EyeOff, Loader2 } from "lucide-react";
import AppButton from "../ui/AppButton.jsx";
import { successButtonLoadingClassName } from "../../design/tokens.js";
import { cn } from "../../utils/themeClasses.js";
import { Input } from "../forms/shared/FormAtoms.jsx";
import {
  AuthPageLayout,
  AuthScreenHeadline,
  authLoginInputClassName,
  authLabelClassName,
} from "./AuthVisualShell.jsx";

export default function FirstPassword() {
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [msg, setMsg] = useState(null);
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);
  const [caps1, setCaps1] = useState(false);
  const [caps2, setCaps2] = useState(false);
  const [saving, setSaving] = useState(false);
  const mountedRef = useRef(true);
  const navTimerRef = useRef(null);
  const nav = useNavigate();
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (navTimerRef.current) clearTimeout(navTimerRef.current);
    };
  }, []);

  const rules = useMemo(
    () => [
      { key: "len", ok: p1.length >= 8, text: "Mindestens 8 Zeichen" },
      { key: "letter", ok: /[A-Za-z]/.test(p1), text: "Mindestens 1 Buchstabe (A–Z)" },
      { key: "digit", ok: /\d/.test(p1), text: "Mindestens 1 Zahl (0–9)" },
    ],
    [p1],
  );
  const allOk = rules.every(r => r.ok);
  const same = p1 === p2;

  const strength = useMemo(() => {
    let s = 0;
    if (p1.length >= 8) s++;
    if (p1.length >= 12) s++;
    if (/[A-Za-z]/.test(p1) && /\d/.test(p1)) s++;
    if (/[^A-Za-z0-9]/.test(p1)) s++;
    return Math.min(s, 4);
  }, [p1]);
  useEffect(() => {
    if (msg) setMsg(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p1, p2]);
  const onCapsCheck1 = useCallback(e => setCaps1(e.getModifierState && e.getModifierState("CapsLock")), []);
  const onCapsCheck2 = useCallback(e => setCaps2(e.getModifierState && e.getModifierState("CapsLock")), []);
  const submit = async e => {
    e.preventDefault();
    if (saving) return;
    if (!same) {
      const t = "Passwörter stimmen nicht überein.";
      setMsg({ text: t, type: "error" });
      showError(t);
      return;
    }
    if (!allOk) {
      const t = "Passwort zu schwach – siehe Anforderungen unten.";
      setMsg({ text: t, type: "error" });
      showError(t);
      return;
    }
    try {
      if (mountedRef.current) setSaving(true);
      const { data } = await promise(
        api.post("/password/change-first", {
          newPassword: p1,
        }),
        {
          loading: "Speichere…",
          success: "Passwort gesetzt.",
          error: "srv.error",
        },
      );
      if (data?.erfolg) {
        if (mountedRef.current) setMsg({ text: "Passwort geändert. Weiterleitung …", type: "success" });
        if (navTimerRef.current) clearTimeout(navTimerRef.current);
        navTimerRef.current = setTimeout(async () => {
          if (!mountedRef.current) return;
          try {
            const check = await api.get("/check-login", { silent: true });
            nav("/");
          } catch {
            nav("/");
          }
        }, 600);
        return;
      }
      const t = data?.msg || "Konnte Passwort nicht ändern.";
      if (mountedRef.current) setMsg({ text: t, type: "error" });
      showError(t);
    } catch (e) {
      const status = e?.response?.status;
      const serverMsg = e?.response?.data?.msg || e?.response?.data?.message;
      const t =
        status === 401
          ? "Nicht eingeloggt oder Session abgelaufen (401)."
          : status === 403
            ? "Zugriff verweigert (403)."
            : status === 409
              ? "Konflikt – Passwort konnte nicht gesetzt werden (409)."
              : status === 429
                ? "Zu viele Versuche – bitte kurz warten."
                : serverMsg || "Fehler beim Ändern.";
      if (mountedRef.current) setMsg({ text: t, type: "error" });
      handleError(e, "srv.error");
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };
  const color = useMemo(() => {
    if (msg?.type === "error") return "text-red-400";
    if (msg?.type === "success") return "text-green-400";
    return "text-blue-200/80";
  }, [msg?.type]);

  const iconTone = saving ? "!text-white/20" : "";
  const fieldBase = `${authLoginInputClassName} !pr-12`;

  return (
    <AuthPageLayout docTitle="Erstpasswort • WireTrack" contentClassName="space-y-3.5 sm:space-y-6">
      <AuthScreenHeadline
        variant="hero"
        title="Neues Passwort setzen"
        description="Einmalige Änderung vor dem ersten Zugriff auf dein Konto."
      />

      {msg?.text ? (
        <p className={`text-sm text-center min-h-[1.25rem] ${color}`} aria-live="polite">
          {msg.text}
        </p>
      ) : null}

      <form onSubmit={submit} className="space-y-3 sm:space-y-[1.125rem]" aria-busy={saving ? "true" : "false"}>
        <div>
          <label htmlFor="pw1" className={authLabelClassName}>
            Neues Passwort
          </label>
          <div className="relative group">
            <span
              className="pointer-events-none absolute left-3.5 top-1/2 flex h-[22px] w-[22px] -translate-y-1/2 items-center justify-center"
              aria-hidden
            >
              <Lock
                className={`login-field-icon login-field-icon--lock h-[18px] w-[18px] shrink-0 text-white/25 transition-colors group-focus-within:text-blue-500 ${iconTone}`}
              />
            </span>
            <Input
              id="pw1"
              type={show1 ? "text" : "password"}
              className={fieldBase}
              placeholder="••••••••"
              value={p1}
              onChange={e => setP1(e.target.value)}
              onKeyUp={onCapsCheck1}
              autoComplete="new-password"
              disabled={saving}
            />
            <AppButton
              type="button"
              variant="ghost"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-2 rounded-lg min-h-0 h-auto w-auto border-0 shadow-none !text-white/30 hover:!text-white/70 hover:!bg-white/5"
              onClick={() => setShow1(v => !v)}
              aria-label={show1 ? "Passwort verbergen" : "Passwort anzeigen"}
              tabIndex={-1}
            >
              {show1 ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}
            </AppButton>
          </div>
          {caps1 && <p className="text-xs mt-1 font-medium text-amber-300/90">Feststelltaste ist aktiv.</p>}
        </div>

        <StrengthBar strength={strength} />

        <div>
          <label htmlFor="pw2" className={authLabelClassName}>
            Wiederholen
          </label>
          <div className="relative group">
            <span
              className="pointer-events-none absolute left-3.5 top-1/2 flex h-[22px] w-[22px] -translate-y-1/2 items-center justify-center"
              aria-hidden
            >
              <Lock
                className={`login-field-icon login-field-icon--lock h-[18px] w-[18px] shrink-0 text-white/25 transition-colors group-focus-within:text-blue-500 ${iconTone}`}
              />
            </span>
            <Input
              id="pw2"
              type={show2 ? "text" : "password"}
              className={fieldBase}
              placeholder="••••••••"
              value={p2}
              onChange={e => setP2(e.target.value)}
              onKeyUp={onCapsCheck2}
              autoComplete="new-password"
              disabled={saving}
            />
            <AppButton
              type="button"
              variant="ghost"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-2 rounded-lg min-h-0 h-auto w-auto border-0 shadow-none !text-white/30 hover:!text-white/70 hover:!bg-white/5"
              onClick={() => setShow2(v => !v)}
              aria-label={show2 ? "Passwort verbergen" : "Passwort anzeigen"}
              tabIndex={-1}
            >
              {show2 ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}
            </AppButton>
          </div>
          {caps2 && <p className="text-xs mt-1 font-medium text-amber-300/90">Feststelltaste ist aktiv.</p>}
        </div>

        <ul className="text-xs mb-1 space-y-1">
          {rules.map(r => (
            <li key={r.key} className={`flex items-center gap-2 ${r.ok ? "text-green-400" : "text-blue-100/70"}`}>
              <span>{r.ok ? "✓" : "✗"}</span>
              <span>{r.text}</span>
            </li>
          ))}
          <li className={`flex items-center gap-2 ${same ? "text-green-400" : "text-blue-100/70"}`}>
            <span>{same ? "✓" : "✗"}</span>
            <span>Beide Eingaben identisch</span>
          </li>
        </ul>

        <AppButton
          type="submit"
          variant="primary"
          className={cn(
            "login-submit-premium relative overflow-hidden w-full justify-center gap-2.5 font-semibold tracking-tight max-sm:min-h-[50px] max-sm:text-[15px] min-h-[52px] text-[15px] sm:min-h-[54px] sm:text-base rounded-[1rem] disabled:!opacity-100 wrtk-btn",
            (!allOk || !same) && !saving ? "cursor-not-allowed" : "",
            saving && successButtonLoadingClassName,
          )}
          disabled={!allOk || !same || saving}
        >
          {saving ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin shrink-0" />
              Speichern …
            </>
          ) : (
            "Speichern"
          )}
        </AppButton>
      </form>
    </AuthPageLayout>
  );
}

function StrengthBar({ strength }) {
  const labels = ["Sehr schwach", "Schwach", "Okay", "Gut", "Stark"];
  const pct = (strength / 4) * 100;
  return (
    <div>
      <div className="h-2 w-full rounded overflow-hidden bg-white/10">
        <div
          className={`h-2 transition-[width] duration-200 ${
            strength <= 1 ? "bg-red-500" : strength === 2 ? "bg-yellow-500" : strength === 3 ? "bg-green-500" : "bg-blue-500"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="text-[11px] mt-1 font-medium text-blue-200/70">{labels[strength] || labels[0]}</div>
    </div>
  );
}
